import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import SecurityModalWrapperContainer from '../pages/accounts/settings/MFA/securityModalWrapper';
import { RootState } from '../store';
import { useAppDispatch } from '../hooks';
import {
  handleValueChange,
  securityModalChange
} from '../pages/accounts/settings/reducer';

const withMfaCheck = <P extends object>(
  WrappedComponent: React.ComponentType<P>
) => {
  return (props: P) => {
    const dispatch = useAppDispatch();

    const {
      account,
      organization,
      campaigns,
      campaignOrganizers,
      requiresMfaEnrollment,
      requiresPasswordReset
    } = useSelector((state: RootState) => state.session);
    const { MFA } = useSelector((state: RootState) => state.accountSettings);

    useEffect(() => {
      // Password first, enforced server-side: MFA setup is refused outright while a rotation is
      // outstanding, so raising the enrolment wall here would offer a flow the API will reject.
      if (requiresPasswordReset) {
        return;
      }

      // The early exit is why a system administrator never tripped this: they have no
      // organization, campaign or campaignOrganizer to speak of. The server-side flag stands
      // on its own, so it has to be tested before the exit rather than inside it.
      if (
        !account ||
        (!requiresMfaEnrollment &&
          !organization &&
          !campaigns &&
          !campaignOrganizers)
      ) {
        return;
      }

      const linkedMfaActive =
        organization?.hasMfaActive ||
        campaigns?.some((x: any) => x?.organization?.hasMfaActive) ||
        campaignOrganizers?.some(
          (x: any) => x?.isAtLeastOneLinkedOrganizationMfaActive
        );

      // we want to force the user to sign up for MFA if the organization has MFA active AND the user has NOT setup MFA
      // requiresMfaEnrollment is the server's own answer, ORed in rather than replacing the
      // legacy check so an older API keeps behaving exactly as it did.
      if (
        !MFA.open &&
        !account?.hasMfaActive &&
        (requiresMfaEnrollment || linkedMfaActive)
      ) {
        dispatch(securityModalChange(true));
        dispatch(handleValueChange('MFA.forceMfa', true));
      }
    }, [
      account?.hasMfaActive,
      organization,
      campaigns,
      campaignOrganizers,
      requiresMfaEnrollment,
      requiresPasswordReset,
      MFA?.open,
      dispatch
    ]);

    return (
      <>
        <SecurityModalWrapperContainer />
        <WrappedComponent {...props} />
      </>
    );
  };
};

// Don't really need a HOC, but using to keep consistent with architecture
export default withMfaCheck;
